import { useMemo, useState } from "react";
import { DashboardLayout } from "@/components/layout";
import { DashboardToolbar, type DeviceSegment } from "@/components/dashboard";
import { UserMenu } from "@/components/auth/UserMenu";
import {
  AuthenticationSection,
  ConversionSection,
  Ga4AcquisitionSection,
  Ga4AudienceSection,
  Ga4EngagementSection,
  Ga4TrafficPatternsSection,
  JourneyFunnelSection,
  PerformanceSection,
  PostLoginSection,
  QmFrictionSection,
  QmFunnelSection,
  ReservationUsersSection,
  TrafficSection,
} from "@/components/sections";
import {
  DEFAULT_PRESET_ID,
  findPreset,
  presetToRange,
} from "@/lib/range-presets";

export function MetricsDashboardPage() {
  const [presetId, setPresetId] = useState<string>(DEFAULT_PRESET_ID);
  const [refreshTick, setRefreshTick] = useState(0);
  const [device, setDevice] = useState<DeviceSegment>(null);

  const range = useMemo(() => {
    const preset = findPreset(presetId) ?? findPreset(DEFAULT_PRESET_ID)!;
    return presetToRange(preset);
    // presetToRange reads `new Date()` internally; refreshTick is the trigger
    // the linter can't see, so it must be in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, refreshTick]);

  return (
    <DashboardLayout>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-fg-primary">
            Operations <span className="text-info-text">Dashboard</span>
          </h1>
          <p className="mt-1 max-w-2xl text-xs text-fg-faint">
            Live view of the Signature Aviation customer-facing site —
            real Google Analytics 4 audience &amp; conversions, plus
            reservation funnel, login health, and Core Web Vitals from
            Azure telemetry. Each panel is tagged with its data source.
          </p>
          <p className="mt-1 text-[11px] text-fg-faint/80">
            Showing{" "}
            <span className="font-medium text-fg-muted">
              {new Date(range.from).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {" – "}
              {new Date(range.to).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
            . GA4 numbers match Google Analytics for the same dates; numbers may differ if you compare a different range there.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <UserMenu />
          <DashboardToolbar
            presetId={presetId}
            onPresetChange={setPresetId}
            onRefresh={() => setRefreshTick((t) => t + 1)}
            device={device}
            onDeviceChange={setDevice}
          />
        </div>
      </header>

      {/* Real GA4 production analytics */}
      <Ga4AudienceSection range={range} device={device} />
      <Ga4TrafficPatternsSection range={range} device={device} />
      <Ga4AcquisitionSection range={range} />
      <Ga4EngagementSection range={range} />

      {/* UC5 telemetry (Azure Log Analytics) */}
      <TrafficSection range={range} />
      <JourneyFunnelSection range={range} />
      <ConversionSection range={range} />
      <ReservationUsersSection range={range} />
      <AuthenticationSection range={range} />
      <PostLoginSection range={range} />
      <PerformanceSection range={range} />

      {/* QuantumMetric user-friction (Use Case A1) */}
      <QmFrictionSection range={range} />

      {/* QuantumMetric reservation/quote funnel (Use Case A2) */}
      <QmFunnelSection range={range} />
    </DashboardLayout>
  );
}
